import { useRef, useMemo, useState, useEffect } from 'react';
import ReactPlayer from 'react-player/file';
import cm from 'classnames';
import Player from 'react-player/file';

import { EFFECT_VALUE_MAX } from 'constants/params';
import { ConfigFlags as ClipConfigFlags } from 'services/clip.service';
import { ConfigFlags as FileConfigFlags } from 'services/file.service';
import { AspectRatioStory } from 'services/story.service';
import { useAppDispatch, useAppSelector } from 'store/store-hooks';

import { selectClipData, getInternalPlayer } from 'components/app/_helpers/_app.helper';
import { useCssRotate } from 'components/app/_mediator/css-rotate/use-css-rotate';
import { useCssFilter } from 'components/app/_mediator/css-filter/use-css-filter';
import { useCssFilterClip } from 'components/app/_mediator/css-filter/use-css-filter-clip';
import { setPanZoomRotateListener } from 'components/app/editor/_mediator/editor.slice';
import { PanZoom } from 'components/app/editor/pan-zoom/pan-zoom';

import { isImage, isLinkedGroup } from './_mediator/sb-player.helper';
import { PlaybackElement } from './_mediator/sb-player.slice';
import { useTransitionEnd } from './lib/use-transition-end';

import { SbPlayerImg } from './sb-player-img';
import { SbPlayerVideo } from './sb-player-video';

import styles from './_sb-player.module.scss';
import { SbPanZoom } from '../sb-pan-zoom/sb-pan-zoom';

interface Props {
  index: Index;
  playbackElement: PlaybackElement;
}

export const SbPlayerElement = ({ playbackElement, index }: Props) => {
  const configPanel = useAppSelector((state) => state.editor.configPanel);
  const panZoomControl = useAppSelector((state) => state.sb.panZoomControl);
  const configs = useAppSelector((state) => state.app__files.configs);
  const clipsMap = useAppSelector((state) => state.editor.clipsMap);
  const storyConfig = useAppSelector((state) => state.editor.storyConfig);
  const dispatch = useAppDispatch();

  const { activePlayerIndex, dampingPlayerIndex, linkedGroupIndex, isEmptyCell } = useAppSelector(
    (state) => state.sb__player
  );

  const transitionElem = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const playerRef = useRef<ReactPlayer>(null);

  useTransitionEnd(transitionElem, index);

  const [init, setInit] = useState(false);

  const isImg = useMemo(() => isImage(playbackElement), [playbackElement]);

  const { isActive, isDamping } = useMemo(() => {
    return {
      isActive: activePlayerIndex === index && !isEmptyCell,
      isDamping: dampingPlayerIndex === index,
    };
  }, [activePlayerIndex, isEmptyCell, dampingPlayerIndex]);

  const clipId = useMemo(() => {
    if (!playbackElement) return;
    if (isLinkedGroup(playbackElement)) {
      if (!isActive) {
        if (isDamping) return playbackElement[playbackElement.length - 1];
        return playbackElement[0];
      }
      return playbackElement[isActive ? linkedGroupIndex ?? 0 : 0];
    }
    return playbackElement as ClipId;
  }, [playbackElement, linkedGroupIndex, isDamping]);

  const clipData = useMemo(() => {
    if (!clipId || !clipsMap) return;
    return selectClipData(clipId, clipsMap);
  }, [clipId, clipId && clipsMap?.[clipId]]);

  const fileConfig = useMemo(() => {
    if (!clipData) return;
    return configs[clipData.mediaFileId];
  }, [clipData, configs]);

  const volume = useMemo(() => {
    if (clipData?.config.flags.includes(ClipConfigFlags.volume)) return clipData.config.volume / 100;
    else if (fileConfig?.flags.includes(FileConfigFlags.volume)) return fileConfig.volume / 100;
    else return EFFECT_VALUE_MAX / 100;
  }, [clipData, fileConfig]);

  const isPanZoom = useMemo(() => {
    return clipData?.config.flags.includes(ClipConfigFlags.panZoomRotate) ?? false;
  }, [clipData]);

  const contentAR = useMemo(() => {
    const ref = isImg ? imgRef : playerRef;
    return getContentAR(ref.current);
  }, [init]);

  const contentScale = useMemo(() => {
    return getContentScale(contentAR, storyConfig?.aspectRatio);
  }, [contentAR, storyConfig]);

  const transformStyle = useMemo(() => {
    return getTransformStyle(contentScale);
  }, [contentScale]);

  const cssFilterElem = useCssFilter({
    brightness: useCssFilterClip('brightness', clipData, fileConfig),
    contrast: useCssFilterClip('contrast', clipData, fileConfig),
  });

  const rotateData = useCssRotate(cssFilterElem, { fileConfig, clipData }, isActive);

  useEffect(() => {
    if (isActive) dispatch(setPanZoomRotateListener(rotateData));
  }, [isActive]);

  return (
    <div
      ref={transitionElem}
      className={cm(styles.player, {
        [styles['player--active']]: isActive,
        [styles['player--damping']]: isDamping,
        [styles['player--panZoomControl']]: panZoomControl,
      })}
      style={transformStyle}
    >
      <div ref={cssFilterElem} className={styles.player__cssFilter}>
        {isImg && <SbPlayerImg imgRef={imgRef} clipData={clipData} setInitPlayer={setInit} />}
        <SbPlayerVideo
          playerRef={playerRef}
          setInitPlayer={setInit}
          playbackElement={isImg ? null : playbackElement}
          volume={volume}
          index={index}
          payload={{
            isActive,
            isDamping,
            clipId: isImg ? undefined : clipId,
            clipData: isImg ? undefined : clipData,
          }}
        />
      </div>
      {isActive && isPanZoom && configPanel === 'clip' && panZoomControl && (
        <PanZoom element={isImg ? imgRef : playerRef} isActive={true} init={init} />
      )}
      <SbPanZoom
        playerIndex={index}
        rotateData={rotateData}
        linkedGroupIndex={linkedGroupIndex}
        clipData={isPanZoom ? clipData! : null}
        mediaEl={isImg ? imgRef : playerRef}
        isDamping={isDamping}
        isActive={isActive}
        init={init}
      />
    </div>
  );
};

/* H E L P E R S */

const RATIO_9x16 = 9 / 16;
const RATIO_16x9 = 16 / 9;

const getContentAR = (el: HTMLImageElement | Player | null): number => {
  if (el instanceof HTMLImageElement) return el.naturalWidth / el.naturalHeight;
  if (el instanceof Player) {
    const player = getInternalPlayer(el);
    return player ? player.videoWidth / player.videoHeight : 1;
  }
  return 1;
};

const getContentScale = (contentAR: number, target?: AspectRatioStory): number => {
  if (target === AspectRatioStory.is9x16 && RATIO_9x16 < contentAR) {
    return RATIO_9x16 / contentAR;
  }
  if (target === AspectRatioStory.is16x9 && RATIO_16x9 < contentAR) {
    return RATIO_16x9 / contentAR;
  }
  return 1;
};

const getTransformStyle = (scale: number): React.CSSProperties => {
  if (scale === 1) return {};
  const s = scale * 100;
  return { width: `${s}%`, height: `${s}%` };
};
